import React, { useState } from 'react';
import { Badge, IconButton, Menu, MenuItem, Typography, Divider, List, ListItem, ListItemText, ListItemIcon, Box, Button } from '@mui/material';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const GET_UNREAD_NOTIFICATIONS_COUNT = gql`
  query GetUnreadNotificationsCount {
    unreadNotificationsCount
  }
`;

const GET_NOTIFICATIONS = gql`
  query GetNotifications($isRead: Boolean) {
    notifications(isRead: $isRead) {
      id
      notificationType
      title
      message
      isRead
      visitId
      absenceRequestId
      appointmentId
      createdAt
      readAt
    }
  }
`;

const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($id: ID!) {
    markNotificationAsRead(id: $id) {
      success
      message
      notification {
        id
        isRead
      }
    }
  }
`;

const MARK_ALL_NOTIFICATIONS_AS_READ = gql`
  mutation MarkAllNotificationsAsRead {
    markAllNotificationsAsRead {
      success
      message
    }
  }
`;

const DELETE_NOTIFICATION = gql`
  mutation DeleteNotification($id: ID!) {
    deleteNotification(id: $id) {
      success
      message
    }
  }
`;

function NotificationIcon() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  // Query pour le nombre de notifications non lues
  const { data: countData, refetch: refetchCount } = useQuery(GET_UNREAD_NOTIFICATIONS_COUNT, {
    pollInterval: 30000, // Rafraîchir toutes les 30 secondes
  });

  // Query pour les notifications (non lues par défaut)
  const { data: notificationsData, refetch: refetchNotifications } = useQuery(GET_NOTIFICATIONS, {
    variables: { isRead: showAll ? null : false },
    skip: !anchorEl, // Ne charger les notifications que lorsque le menu est ouvert
  });

  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    onCompleted: () => {
      refetchNotifications();
      refetchCount();
    },
  });

  const [markAllAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
    onCompleted: () => {
      refetchNotifications();
      refetchCount();
    },
  });

  const [deleteNotification] = useMutation(DELETE_NOTIFICATION, {
    onCompleted: () => {
      refetchNotifications();
      refetchCount();
    },
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    // Marquer comme lue si elle ne l'est pas déjà
    if (!notification.isRead) {
      markAsRead({ variables: { id: notification.id } });
    }

    // Naviguer vers le détail
    if (notification.visitId) {
      handleClose();
      navigate(`/visits`);
    } else if (notification.absenceRequestId) {
      handleClose();
      navigate(`/absences`);
    } else if (notification.appointmentId) {
      handleClose();
      // Rediriger vers la page des rendez-vous avec l'ID en paramètre pour mettre en évidence
      navigate(`/appointments?highlight=${notification.appointmentId}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteNotification({ variables: { id } });
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
    }
  };

  const unreadCount = countData?.unreadNotificationsCount || 0;
  const notifications = notificationsData?.notifications || [];

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 500,
            width: 400,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Notifications</Typography>
            <Box>
              <Button
                size="small"
                onClick={() => setShowAll(!showAll)}
                sx={{ mr: 1 }}
              >
                {showAll ? 'Non lues' : 'Toutes'}
              </Button>
              {unreadCount > 0 && !showAll && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={handleMarkAllAsRead}
                >
                  Tout lire
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        <Divider />
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText primary="Aucune notification" />
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                button
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  backgroundColor: notification.isRead ? 'transparent' : '#f5f5f5',
                  '&:hover': {
                    backgroundColor: notification.isRead ? '#f0f0f0' : '#e0e0e0',
                  },
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(e) => handleDelete(e, notification.id)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {notification.isRead ? (
                    <NotificationsIcon />
                  ) : (
                    <NotificationImportantIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={notification.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" display="block">
                        {notification.message}
                      </Typography>
                      <Typography component="span" variant="caption" color="textSecondary">
                        {format(new Date(notification.createdAt), 'PPPp', { locale: fr })}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Menu>
    </>
  );
}

export default NotificationIcon;

